// Event types for producer courses & events feature

export type EventType = 'course' | 'event' | 'workshop' | 'dinner' | 'outing';
export type EventAudience = 'public' | 'authenticated';
export type BookingMode = 'none' | 'optional' | 'required';
export type EventStatus = 'draft' | 'public' | 'cancelled';
export type BookingStatus = 'pending' | 'confirmed' | 'cancelled';
export type OccurrenceStatus = 'scheduled' | 'cancelled';

export interface ProducerEvent {
  id: string;
  producer_id: string;
  title: string;
  event_type: EventType;
  description: string | null;
  start_date: string;
  end_date: string;
  is_recurring: boolean;
  recurrence_rule: string | null;
  use_producer_address: boolean;
  custom_address: string | null;
  custom_lat: number | null;
  custom_lng: number | null;
  price_ore: number;
  currency: string;
  max_spots: number;
  booking_deadline_hours: number;
  is_free: boolean;
  booking_mode: BookingMode;
  status: EventStatus;
  audience: EventAudience;
  learning_points: string[] | null;
  includes_farm_sale: boolean;
  includes_farm_sale_alcohol: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface ProducerEventWithBookings extends ProducerEvent {
  booked_spots: number;
  is_full: boolean;
  spots_left: number | null;
  occurrences_count: number | null;
}

export interface EventOccurrence {
  id: string;
  event_id: string;
  start_at: string;
  end_at: string;
  capacity: number | null;
  status: OccurrenceStatus;
  created_at: string;
  updated_at: string;
}

export interface EventOccurrenceWithBookings extends EventOccurrence {
  booked_spots: number;
}

export interface EventBooking {
  id: string;
  event_id: string;
  producer_id: string;
  occurrence_id: string | null;
  user_id: string | null;
  guest_name: string | null;
  guest_email: string;
  spots: number;
  status: BookingStatus;
  confirmation_token: string;
  booking_code: string;
  confirmation_sent_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateEventData {
  producer_id: string;
  title: string;
  event_type: EventType;
  description?: string | null;
  start_date: string;
  end_date: string;
  is_recurring?: boolean;
  recurrence_rule?: string;
  use_producer_address?: boolean;
  custom_address?: string | null;
  custom_lat?: number;
  custom_lng?: number;
  price_ore?: number;
  currency?: string;
  max_spots?: number;
  booking_deadline_hours?: number;
  is_free?: boolean;
  status?: EventStatus;
}

export interface CreateBookingData {
  event_id: string;
  producer_id: string;
  occurrence_id?: string | null;
  user_id?: string | null;
  guest_name?: string;
  guest_email: string;
  spots: number;
}

// Helper labels (Swedish)
export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  course: 'Kurs',
  event: 'Aktivitet',
  workshop: 'Workshop',
  dinner: 'Middag / Provning',
  outing: 'Utflykt',
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Utkast',
  public: 'Publicerad',
  cancelled: 'Inställd',
};

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Väntande',
  confirmed: 'Bekräftad',
  cancelled: 'Avbokad',
};

export const OCCURRENCE_STATUS_LABELS: Record<OccurrenceStatus, string> = {
  scheduled: 'Planerad',
  cancelled: 'Inställd',
};
